import React, { useEffect, useMemo, useState } from 'react';
import {
  getWebRtcPolicyConfig, setWebRtcPolicyConfig,
  WEBRTC_POLICY, WebRtcPolicyValue, WebRtcPolicyConfig,
  policyLabel, stroopsToDisplay, displayToStroops, formatDuration,
} from '../services/webrtcPolicyService';

/**
 * WebRtcPolicySelector
 *
 * Full admin surface for the per-country paid-live-session policy.
 * Shows the V1 tri-state + cost + base duration up front, with all
 * V2 fields (viewer-paid extensions, per-extension cost / seconds,
 * total-duration cap, max extensions, min-extension quantum,
 * platform/broadcaster revenue split in basis points) grouped in a
 * collapsible "Advanced" panel so global/country admins can configure
 * the fine-grained controls without overwhelming the default flow.
 *
 * Used inside:
 *   - CountryManagementOverlay (country & global admins — editable)
 *   - CountryProfileOverlay    (regular users — read-only view)
 *
 * Emits `onChange` with the updated config whenever an admin saves a
 * new value so parent screens can refresh derived UI (live badges,
 * price hints on the profile overlay, extend-button gating in the
 * live HUD, etc.) without a second round-trip.
 */

export interface WebRtcPolicySelectorProps {
  country: number;
  canEdit: boolean;
  walletAddress?: string;
  onChange?: (config: WebRtcPolicyConfig) => void;
}

const ALL_VALUES: WebRtcPolicyValue[] = [
  WEBRTC_POLICY.NOT_SET, WEBRTC_POLICY.ALLOWED, WEBRTC_POLICY.DISALLOWED,
];

const WebRtcPolicySelector: React.FC<WebRtcPolicySelectorProps> = ({
  country, canEdit, walletAddress, onChange,
}) => {
  const [config, setConfig] = useState<WebRtcPolicyConfig | null>(null);
  const [policyValue, setPolicyValue] = useState<WebRtcPolicyValue>(WEBRTC_POLICY.NOT_SET);
  const [costInput, setCostInput] = useState<string>('0');
  const [durationInput, setDurationInput] = useState<string>('0');

  // ── V2 form state ─────────────────────────────────────────────────────────
  const [extensionsEnabled, setExtensionsEnabled] = useState<boolean>(true);
  const [extensionCostInput, setExtensionCostInput] = useState<string>('0');
  const [extensionDurationInput, setExtensionDurationInput] = useState<string>('0');
  const [maxTotalDurationInput, setMaxTotalDurationInput] = useState<string>('0');
  const [maxExtensionsInput, setMaxExtensionsInput] = useState<string>('0');
  const [minExtensionSecondsInput, setMinExtensionSecondsInput] = useState<string>('0');
  const [platformBpsInput, setPlatformBpsInput] = useState<string>('0');
  const [broadcasterBpsInput, setBroadcasterBpsInput] = useState<string>('0');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Any non-default V2 field opens the Advanced panel on mount so
  // operators don't accidentally "lose" state they previously wrote.
  const advancedHasValues = (c: WebRtcPolicyConfig | null) =>
    !!c && (
      c.extensionsEnabled === false ||
      (c.extensionCost && c.extensionCost !== '' && c.extensionCost !== '0') ||
      (c.extensionDurationSeconds || 0) > 0 ||
      (c.maxTotalDurationSeconds || 0) > 0 ||
      (c.maxExtensions || 0) > 0 ||
      (c.minExtensionSeconds || 0) > 0 ||
      (c.platformBps || 0) > 0 ||
      (c.broadcasterBps || 0) > 0
    );

  useEffect(() => {
    let active = true;
    getWebRtcPolicyConfig(country).then((c) => {
      if (!active) return;
      setConfig(c);
      setPolicyValue(c.policy);
      setCostInput(stroopsToDisplay(c.sessionCost));
      setDurationInput(String(c.maxDurationSeconds || 0));
      setExtensionsEnabled(c.extensionsEnabled !== false);
      setExtensionCostInput(stroopsToDisplay(c.extensionCost || '0'));
      setExtensionDurationInput(String(c.extensionDurationSeconds || 0));
      setMaxTotalDurationInput(String(c.maxTotalDurationSeconds || 0));
      setMaxExtensionsInput(String(c.maxExtensions || 0));
      setMinExtensionSecondsInput(String(c.minExtensionSeconds || 0));
      setPlatformBpsInput(String(c.platformBps || 0));
      setBroadcasterBpsInput(String(c.broadcasterBps || 0));
      setShowAdvanced(advancedHasValues(c));
      setDirty(false);
    }).catch(() => { if (active) setError('load_failed'); });
    return () => { active = false; };
  }, [country]);

  const parsedBps = useMemo(() => {
    const plat = Number(platformBpsInput) || 0;
    const bcst = Number(broadcasterBpsInput) || 0;
    return { plat, bcst, sum: plat + bcst };
  }, [platformBpsInput, broadcasterBpsInput]);

  const validationError = useMemo(() => {
    if (parsedBps.sum > 10_000) return 'Platform + broadcaster basis points must not exceed 10000 (100%).';
    const nonNegU32 = (v: string, label: string) => {
      if (v === '') return null;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || Math.floor(n) !== n) return `${label} must be a non-negative integer.`;
      return null;
    };
    const checks = [
      nonNegU32(durationInput,            'Max live duration'),
      nonNegU32(extensionDurationInput,   'Extension duration'),
      nonNegU32(maxTotalDurationInput,    'Max total duration'),
      nonNegU32(maxExtensionsInput,       'Max extensions'),
      nonNegU32(minExtensionSecondsInput, 'Min extension seconds'),
      nonNegU32(platformBpsInput,         'Platform bps'),
      nonNegU32(broadcasterBpsInput,      'Broadcaster bps'),
    ];
    return checks.find((e) => e !== null) || null;
  }, [
    parsedBps.sum, durationInput, extensionDurationInput, maxTotalDurationInput,
    maxExtensionsInput, minExtensionSecondsInput, platformBpsInput, broadcasterBpsInput,
  ]);

  async function save() {
    if (!canEdit) return;
    if (validationError) { setError(validationError); return; }
    setBusy(true); setError(null);
    try {
      const costStroops = displayToStroops(costInput);
      const extCostStroops = displayToStroops(extensionCostInput);
      const durationSec = Number(durationInput);
      if (!Number.isFinite(durationSec) || durationSec < 0) throw new Error('Invalid duration');

      await setWebRtcPolicyConfig(country, {
        walletAddress,
        policy: policyValue,
        sessionCost: costStroops,
        maxDurationSeconds: Math.floor(durationSec),
        policyVersion: (config?.policyVersion || 0) + 1,

        extensionsEnabled,
        extensionCost:            extCostStroops,
        extensionDurationSeconds: Math.max(0, Math.floor(Number(extensionDurationInput) || 0)),
        maxTotalDurationSeconds:  Math.max(0, Math.floor(Number(maxTotalDurationInput)  || 0)),
        maxExtensions:            Math.max(0, Math.floor(Number(maxExtensionsInput)     || 0)),
        minExtensionSeconds:      Math.max(0, Math.floor(Number(minExtensionSecondsInput) || 0)),
        platformBps:              Math.max(0, Math.floor(Number(platformBpsInput)       || 0)),
        broadcasterBps:           Math.max(0, Math.floor(Number(broadcasterBpsInput)    || 0)),
      });

      const next = await getWebRtcPolicyConfig(country);
      setConfig(next);
      setDirty(false);
      onChange?.(next);
    } catch (err: any) {
      setError(err?.message || 'save_failed');
    } finally {
      setBusy(false);
    }
  }

  const durationSec = Number(durationInput) || 0;
  const extDurationSec = Number(extensionDurationInput) || 0;

  function revert() {
    if (!config) return;
    setPolicyValue(config.policy);
    setCostInput(stroopsToDisplay(config.sessionCost));
    setDurationInput(String(config.maxDurationSeconds || 0));
    setExtensionsEnabled(config.extensionsEnabled !== false);
    setExtensionCostInput(stroopsToDisplay(config.extensionCost || '0'));
    setExtensionDurationInput(String(config.extensionDurationSeconds || 0));
    setMaxTotalDurationInput(String(config.maxTotalDurationSeconds || 0));
    setMaxExtensionsInput(String(config.maxExtensions || 0));
    setMinExtensionSecondsInput(String(config.minExtensionSeconds || 0));
    setPlatformBpsInput(String(config.platformBps || 0));
    setBroadcasterBpsInput(String(config.broadcasterBps || 0));
    setDirty(false);
    setError(null);
  }

  // Helper: wire onChange + dirty bump in one place so every V2 field
  // doesn't have to repeat the `setDirty(true)` incantation.
  const bind = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setDirty(true);
  };

  return (
    <div style={{ padding: '10px 12px', border: '1px solid #2a3a4a', borderRadius: 8, background: '#0d1721' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>WebRTC live-session policy</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, marginBottom: 10 }}>
        Controls whether live WebRTC broadcasts are allowed for wallets acting under this
        country's jurisdiction, the cost to start one, and the maximum live duration.
        <em style={{ marginLeft: 4 }}>Not set</em> blocks session creation; operators must
        explicitly opt in by selecting Allowed or Disallowed.
      </div>

      {/* --- Tri-state policy --- */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ALL_VALUES.map((v) => {
          const active = v === policyValue;
          return (
            <button
              key={v}
              disabled={!canEdit || busy}
              onClick={() => { setPolicyValue(v); setDirty(true); }}
              style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 12,
                cursor: canEdit ? 'pointer' : 'default',
                background: active ? '#1d4ed8' : '#111c26',
                color:      active ? '#fff'    : '#cbd5e1',
                border: '1px solid ' + (active ? '#1d4ed8' : '#2a3a4a'),
                opacity: canEdit ? 1 : 0.7,
              }}
            >{policyLabel(v)}</button>
          );
        })}
      </div>

      {/* --- Cost + duration --- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
        <div>
          <label style={labelStyle}>Session cost (XLM) — 0 means free</label>
          <input
            type="number" step="0.0000001" min="0"
            value={costInput}
            disabled={!canEdit || busy}
            onChange={bind(setCostInput)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Max live duration (seconds)</label>
          <input
            type="number" step="1" min="0"
            value={durationInput}
            disabled={!canEdit || busy}
            onChange={bind(setDurationInput)}
            style={inputStyle}
            placeholder="0 = use backend safe fallback"
          />
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
            {durationSec > 0
              ? `≈ ${formatDuration(durationSec)} per live session`
              : `Not set — backend falls back to ${formatDuration(config?.effectiveMaxDurationSeconds || 3600)}`}
          </div>
        </div>
      </div>

      {/* --- Advanced (V2 extension + revenue split) --- */}
      <div style={{ marginTop: 14, borderTop: '1px solid #1e2937', paddingTop: 10 }}>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          style={{
            background: 'transparent', border: 'none', color: '#93c5fd',
            cursor: 'pointer', fontSize: 12, padding: 0,
          }}
        >{showAdvanced ? '▾ Hide advanced controls' : '▸ Show advanced controls (extensions, caps, revenue split)'}</button>

        {showAdvanced && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
              Fine-grained caps that govern viewer-paid extensions and split of
              paid-session revenue between the platform treasury and the
              broadcaster.  Zeros act as sentinels: an unset limit means
              "unlimited", an unset cost/seconds means "reuse the session values".
            </div>

            {/* Viewer extensions toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#cbd5e1', marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={extensionsEnabled}
                disabled={!canEdit || busy}
                onChange={(e) => { setExtensionsEnabled(e.target.checked); setDirty(true); }}
              />
              Viewers may pay to extend this broadcaster's session
            </label>

            {/* Extension cost + seconds */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Extension cost (XLM) — 0 means "reuse session cost"</label>
                <input
                  type="number" step="0.0000001" min="0"
                  value={extensionCostInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setExtensionCostInput)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Extension duration (seconds) — 0 means "reuse max duration"</label>
                <input
                  type="number" step="1" min="0"
                  value={extensionDurationInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setExtensionDurationInput)}
                  style={inputStyle}
                />
                {extDurationSec > 0 && (
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
                    ≈ {formatDuration(extDurationSec)} added per extension
                  </div>
                )}
              </div>
            </div>

            {/* Caps */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
              <div>
                <label style={labelStyle}>Max total duration (sec) — 0 = unlimited</label>
                <input
                  type="number" step="1" min="0"
                  value={maxTotalDurationInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setMaxTotalDurationInput)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Max extensions — 0 = unlimited</label>
                <input
                  type="number" step="1" min="0"
                  value={maxExtensionsInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setMaxExtensionsInput)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Min extension (sec) — 0 = no floor</label>
                <input
                  type="number" step="1" min="0"
                  value={minExtensionSecondsInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setMinExtensionSecondsInput)}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Revenue split */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <div>
                <label style={labelStyle}>Platform share (basis points, 10000 = 100%)</label>
                <input
                  type="number" step="1" min="0" max="10000"
                  value={platformBpsInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setPlatformBpsInput)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Broadcaster share (basis points)</label>
                <input
                  type="number" step="1" min="0" max="10000"
                  value={broadcasterBpsInput}
                  disabled={!canEdit || busy}
                  onChange={bind(setBroadcasterBpsInput)}
                  style={inputStyle}
                />
                <div style={{ fontSize: 11, color: parsedBps.sum > 10_000 ? '#f87171' : '#64748b', marginTop: 3 }}>
                  Sum: {parsedBps.sum} / 10000
                  {parsedBps.sum > 10_000 ? ' — invalid' : (parsedBps.sum === 0 ? ' (100% to treasury — legacy behaviour)' : '')}
                </div>
              </div>
            </div>
            {parsedBps.sum > 0 && parsedBps.sum <= 10_000 && (
              <div style={{
                marginTop: 8, padding: '6px 8px', borderRadius: 4,
                background: '#101a24', border: '1px solid #1e293b',
                fontSize: 11, color: '#cbd5e1',
              }}>
                <strong style={{ color: '#e2e8f0' }}>Settlement preview:</strong>
                {' '}each paid session routes{' '}
                <strong>{(parsedBps.bcst / 100).toFixed(2)}%</strong> to the broadcaster
                and <strong>{((10_000 - parsedBps.bcst) / 100).toFixed(2)}%</strong>{' '}
                stays with the treasury.{' '}
                <span style={{ color: '#94a3b8' }}>
                  (Settlement is best-effort — if the broadcaster uses a smart-account
                  (C…) or the outbound payout fails, the full amount stays with the
                  treasury and ops can sweep later from the audit log.)
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- Current effective state --- */}
      {config && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
          Currently on-chain:
          <strong style={{ color: '#cbd5e1', marginLeft: 4 }}>
            {policyLabel(config.policy)}
          </strong>
          {' · cost '}
          <strong style={{ color: '#cbd5e1' }}>{stroopsToDisplay(config.sessionCost)} XLM</strong>
          {' · max '}
          <strong style={{ color: '#cbd5e1' }}>
            {config.maxDurationSeconds > 0
              ? formatDuration(config.maxDurationSeconds)
              : `unset (fallback ${formatDuration(config.effectiveMaxDurationSeconds)})`}
          </strong>
          {advancedHasValues(config) && (
            <span>
              {' · ext '}
              <strong style={{ color: '#cbd5e1' }}>
                {config.extensionsEnabled === false ? 'off' : 'on'}
              </strong>
              {(config.maxExtensions || 0) > 0
                ? <span>, max {config.maxExtensions}</span>
                : null}
              {(config.maxTotalDurationSeconds || 0) > 0
                ? <span>, cap {formatDuration(config.maxTotalDurationSeconds!)}</span>
                : null}
              {((config.platformBps || 0) + (config.broadcasterBps || 0)) > 0
                ? <span> · split {(config.platformBps || 0) / 100}% / {(config.broadcasterBps || 0) / 100}%</span>
                : null}
            </span>
          )}
          {config.policyVersion ? <span> · v{config.policyVersion}</span> : null}
        </div>
      )}

      {/* --- Actions --- */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            onClick={save}
            disabled={busy || !dirty || !!validationError}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: (busy || !dirty || !!validationError) ? '#1e293b' : '#1d4ed8',
              color:      (busy || !dirty || !!validationError) ? '#64748b' : '#fff',
              border: 'none', cursor: (busy || !dirty || !!validationError) ? 'not-allowed' : 'pointer',
            }}
            title={validationError || undefined}
          >{busy ? 'Saving…' : 'Save WebRTC policy'}</button>
          {dirty && (
            <button
              onClick={revert}
              disabled={busy}
              style={{
                padding: '6px 12px', borderRadius: 6, fontSize: 12,
                background: '#111c26', color: '#cbd5e1',
                border: '1px solid #2a3a4a', cursor: 'pointer',
              }}
            >Revert</button>
          )}
        </div>
      )}

      {/* Saving info: explicit because admins watching for Freighter
          will otherwise think the save silently failed. */}
      {canEdit && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
          This writes to the backend policy mirror — no on-chain transaction
          is required, so your wallet will <em>not</em> open Freighter / the
          passkey prompt. Authorisation is by country/global admin role
          attached to <code>{walletAddress
            ? `${walletAddress.slice(0, 6)}…${walletAddress.slice(-4)}`
            : 'the connected wallet'}</code>.
        </div>
      )}

      {(error || validationError) && (
        <div style={{ color: '#f87171', fontSize: 12, marginTop: 6, wordBreak: 'break-word' }}>
          {error || validationError}
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 7px',
  fontSize: 12,
  backgroundColor: '#111c26',
  border: '1px solid #2a3a4a',
  borderRadius: 4,
  color: '#e2e8f0',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: '#94a3b8',
  marginBottom: 3,
};

export default WebRtcPolicySelector;
